import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import { ActionBar } from '@/components/action-bar/ActionBar'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { AiChat, OpenNew } from '@/assets/icons'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { selectFileListRequest } from '@/user_files/files/store/selectors'
import { UserFilesOperationOverlay } from '@/user_files/batching/components/UserFilesOperationOverlay'
import { DynamicContentViewer } from '@/user_files/drive/components/DynamicContentViewer'
import { BottomStickyBar } from '@/user_files/drive/components/BottomStickyBar'
import { selectActiveMatter } from '@/matter/store/selectors'
import { isSampleMatter } from '@/matter/utils/is-sample-matter'
import { canEditMatterSettings } from '@/matter/utils/matter-permissions'
import { useUserFile } from '@/user_files/files/hooks/useUserFiles'
import { useViewUserFile } from '@/user_files/files/hooks/useViewUserFile'
import ErrorCard from '@/components/error/ErrorCard'
import { matterPaths } from '@/routing/paths'
import { useDriveSourceView } from '@/user_files/drive/hooks/useDriveSourceView'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { DriveSyncSetupBanner } from '@/user_files/external-storage/components/DriveSyncSetupBanner'
import { DriveConnectivityChip } from '@/user_files/external-storage/components/DriveConnectivityChip'
import { useMatterDriveConnectivity } from '@/user_files/external-storage/hooks/useMatterDriveConnectivity'
import { useMatterSyncRecovery } from '@/user_files/external-storage/hooks/useMatterSyncRecovery'
import { NoSyncedFolderEmptyState } from '@/user_files/external-storage/components/NoSyncedFolderEmptyState'
import { SyncedFileList } from '@/user_files/external-storage/components/SyncedFileList'
import { openInProviderTab, resolveOpenInProviderFile } from '@/user_files/external-storage/utils/open-in-provider'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { ContextualActionBar, type ContextualAction } from '@/user_files/files/components/ContextualActionBar'
import { useStartChatWithSelectedFiles } from '@/user_files/files/hooks/useStartChatWithSelectedFiles'
import { DriveSourceHeader } from './DriveSourceHeader'
import { MatterFilesSearchBar } from '@/user_files/search/components/MatterFilesSearchBar'
import { useMatterFilesSearch } from '@/user_files/search/hooks/useMatterFilesSearch'
import { useDriveListSort } from '@/user_files/drive/hooks/useDriveListSort'
import { useDriveListFetches } from '@/user_files/drive/hooks/useDriveListFetches'
import { MatterFilesPanel } from './MatterFilesPanel'
import { MatterOcrQuotaStatus } from './MatterOcrQuotaStatus'

/**
 * DriveView
 *
 * Main orchestrator for the user-files drive surface. Routes between the matter's own files
 * (MatterFilesPanel — full CRUD: upload, new folder, rename, move, delete) and a readonly view
 * of its synced DMS files (SyncedFileList), sharing one header (DriveSourceHeader) and one
 * source-view hook (useDriveSourceView) between the two. Presentational/mutation complexity for
 * each source lives in its own component so neither pays for the other's machinery.
 *
 * This component itself keeps only what both sources need: the
 * top-level folder-load error state, and the file-view-from-URL-param effect.
 *
 * Note: Requires a matter to be selected. Files are organized per-matter.
 */
export function DriveView() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { matterId, folderId } = useParams<{ matterId: string; folderId?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  // Redux state
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const fileListRequest = useAppSelector((state) => selectFileListRequest(state, { folderId: currentFolderId }))
  const activeMatter = useAppSelector(selectActiveMatter)
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const canShowConnectivity =
    driveSyncEnabled && !!matterId && matterId === activeMatterId && canEditMatterSettings(activeMatter) && !isSampleMatter(activeMatter)
  const driveConnectivity = useMatterDriveConnectivity(matterId, canShowConnectivity)
  const syncRecovery = useMatterSyncRecovery({
    matterId,
    connectedByUserId: driveConnectivity?.connectedByUserId,
    settingsEntrySource: SettingsEntrySource.DriveViewConnectDrive,
  })
  const viewUserFileId = searchParams.get('viewUserFile')
  const { file: fileToView, loading: fileToViewLoading, error: fileToViewError } = useUserFile(viewUserFileId)
  const viewUserFile = useViewUserFile()

  // The list endpoints own row order, so the sort belongs next to the fetches that carry it rather
  // than inside the table. It persists across folder navigation and across clearing search.
  const sorting = useDriveListSort()
  const { sort } = sorting

  const {
    viewMode,
    sourceResolutionPending,
    setViewMode,
    openExternalFolder,
    showSourceToggle,
    dmsProvider,
    isUnsynced,
    syncedProvider,
    initialSyncInProgress,
    externalBrowseProps,
    syncedSelection,
    externalFolderUrl,
  } = useDriveSourceView(matterId, sort, folderId ? 'matter' : undefined)

  const { isStarting: isStartingAssistantChat, startChat: startSyncedAssistantChat } = useStartChatWithSelectedFiles({
    matterId: matterId ?? '',
    files: externalBrowseProps.files,
    selectedFileIds: syncedSelection.selectedFileIds,
    selectedFolderIds: [],
  })
  const singleSelectedSyncedFile =
    syncedSelection.selectedCount === 1 ? externalBrowseProps.files.find((file) => file.id === syncedSelection.selectedFileIds[0]) : undefined
  const syncedOpenInProvider = resolveOpenInProviderFile(singleSelectedSyncedFile?.external_storage)
  const syncedActions: ContextualAction[] = [
    {
      label: 'Ask Assistant',
      icon: AiChat,
      onClick: startSyncedAssistantChat,
      loading: isStartingAssistantChat,
    },
    {
      label: `Open in ${syncedProvider ? PROVIDER_COMPACT_DISPLAY_NAME[syncedProvider] : 'source'}`,
      icon: syncedProvider ? PROVIDER_ICON_COMPONENTS[syncedProvider] : OpenNew,
      trailingIcon: OpenNew,
      onClick: syncedOpenInProvider ? () => openInProviderTab(syncedOpenInProvider.url) : undefined,
    },
  ]
  // Search is matter-wide, so it belongs to the drive rather than to either source view: the field
  // sits above the source toggle, and committing a query shows results whichever tab is active.
  const search = useMatterFilesSearch()

  // Keeps the Paxton folder fresh while it is visible, including when returning from the DMS tab.
  useDriveListFetches(sort, viewMode === 'matter')

  const connectivityChip = driveConnectivity && (
    <DriveConnectivityChip
      provider={driveConnectivity.provider}
      status={driveConnectivity.status}
      syncedFolderPath={driveConnectivity.syncedFolderPath}
      persistentLabel={viewMode === 'external'}
      isConnector={syncRecovery.isConnector}
      connectorLabel={syncRecovery.connectorLabel}
      onReconnect={syncRecovery.onReconnect}
      onOpenMatterSettings={() => matterId && navigate(matterPaths.settings(matterId))}
      onOpenDisconnectedMatterSettings={syncRecovery.onOpenMatterSettings}
    />
  )

  useEffect(() => {
    if (!viewUserFileId) return
    if (fileToViewLoading) return
    if (!fileToView && !fileToViewError) return

    if (fileToView && !fileToViewError) {
      viewUserFile(fileToView)
    }

    const cleanedParams = new URLSearchParams(searchParams)
    cleanedParams.delete('viewUserFile')
    setSearchParams(cleanedParams, { replace: true })
  }, [viewUserFileId, fileToViewLoading, fileToViewError, fileToView, searchParams, setSearchParams, viewUserFile])

  if (sourceResolutionPending) {
    return (
      <div className="flex flex-1 items-center justify-center" role="status" aria-label="Loading file source">
        <CircularProgress size={24} />
      </div>
    )
  }

  if (fileListRequest.status === 'error' && matterId && viewMode === 'matter') {
    const isMissingOrDenied = fileListRequest.statusCode === 403 || fileListRequest.statusCode === 404
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-6">
        {isMissingOrDenied ? (
          <ErrorCard
            code="404"
            title="Folder not available"
            message="This folder may have been deleted or is no longer accessible to your account."
            actionLabel="Back to Matter Files"
            onClick={() => navigate(matterPaths.userFiles.root(matterId))}
          />
        ) : (
          <ErrorCard
            code="500"
            title="Something went wrong"
            message="We hit an unexpected error loading this folder. Please try again."
            actionLabel="Retry"
            onClick={() => dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId!, sort }))}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <UserFilesOperationOverlay />
      <DynamicContentViewer>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-2 md:pt-3 lg:pt-7">
          <div className="flex w-full min-w-0 flex-1 flex-col min-h-0 px-0 md:px-4 lg:px-10">
            {/* Matter-wide search, above the source toggle it takes over while results show. */}
            <div className="px-4 pb-5 md:px-0">
              <MatterFilesSearchBar
                query={search.query}
                onQueryChange={search.setQuery}
                onSubmit={search.submitQuery}
                onClearSearch={search.clearSearch}
                onOpenSyncedFolder={openExternalFolder}
                onOpenMatterFolder={() => setViewMode('matter')}
              />
            </div>

            {viewMode === 'external' && !search.isActive ? (
              <>
                <div className="px-4 md:px-0">
                  <MatterOcrQuotaStatus syncedFiles={externalBrowseProps.files} />
                </div>
                {syncedSelection.selectedCount > 0 ? (
                  <ContextualActionBar
                    selectedCount={syncedSelection.selectedCount}
                    actions={syncedActions}
                    onClearSelection={syncedSelection.clearSelection}
                  />
                ) : (
                  <ActionBar
                    trailing={connectivityChip}
                    content={
                      <DriveSourceHeader
                        viewMode={viewMode}
                        onChangeViewMode={setViewMode}
                        showSourceToggle={showSourceToggle}
                        externalBreadcrumbItems={externalBrowseProps.breadcrumbItems}
                        dmsProvider={dmsProvider}
                      />
                    }
                    actions={
                      externalFolderUrl && syncedProvider
                        ? [
                            {
                              icon: <OpenNew className="h-4 w-4" />,
                              label: `Open ${PROVIDER_COMPACT_DISPLAY_NAME[syncedProvider]}`,
                              onClick: () => openInProviderTab(externalFolderUrl),
                            },
                          ]
                        : []
                    }
                  />
                )}
                {/* Nothing is mapped, so the list gets no data and never loads — it is here for the
                    table shell, with the folder prompt where the rows would be. */}
                {isUnsynced && matterId ? (
                  <SyncedFileList
                    folders={[]}
                    files={[]}
                    loading={false}
                    onNavigateToFolder={externalBrowseProps.onNavigateToFolder}
                    provider={dmsProvider}
                    emptyState={
                      <div className="p-6">
                        <NoSyncedFolderEmptyState matterId={matterId} />
                      </div>
                    }
                  />
                ) : (
                  <SyncedFileList
                    folders={externalBrowseProps.folders}
                    files={externalBrowseProps.files}
                    loading={externalBrowseProps.loading}
                    blockingError={externalBrowseProps.blockingError}
                    onRetry={externalBrowseProps.onRetry}
                    initialSyncInProgress={initialSyncInProgress}
                    onNavigateToFolder={externalBrowseProps.onNavigateToFolder}
                    provider={syncedProvider}
                    selection={syncedSelection}
                    sorting={sorting.sorting}
                    onSortingChange={sorting.onSortingChange}
                  />
                )}
              </>
            ) : (
              activeMatterId && (
                <MatterFilesPanel
                  activeMatterId={activeMatterId}
                  headerTrailing={connectivityChip}
                  headerProps={{
                    viewMode,
                    onChangeViewMode: setViewMode,
                    showSourceToggle,
                    externalBreadcrumbItems: externalBrowseProps.breadcrumbItems,
                    dmsProvider,
                  }}
                  onOpenSyncedFolder={openExternalFolder}
                  search={search}
                  sorting={sorting}
                  // The setup prompt sits below the header row, so the panel places it rather than the drive.
                  banner={
                    matterId && (
                      <DriveSyncSetupBanner
                        matterId={matterId}
                        matterDriveSyncEntrySource={MatterDriveSyncEntrySource.DriveView}
                        settingsEntrySource={SettingsEntrySource.DriveViewConnectDrive}
                        className="mb-4 px-4 md:px-0"
                      />
                    )
                  }
                />
              )
            )}
          </div>
        </div>
      </DynamicContentViewer>

      {/* Bottom sticky bar */}
      <BottomStickyBar />
    </>
  )
}
